import {
  trackCategoriesLoadPageSectionTiming,
  trackCategoriesLoadPageSectionTimingTraceApiRequestEnd,
  trackCategoriesLoadPageSectionTimingTraceApiRequestStart,
  trackCategoriesLoadPageSectionTimingTraceRendered,
} from "@faire/web-api/events/categories/timing/loadPageSection";
import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { ITraceEventMarker } from "@faire/web/common/pete/TraceEventTracker";
import { useEffectEvent } from "@faire/web/ui/hooks/useEffectEvent";
import isEqual from "lodash/isEqual";
import { useLayoutEffect, useMemo, useRef } from "react";

import { useLayoutElementFilterKeys } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/LayoutElements/__internal__/useLayoutElementFilterKeys";
import { useLayoutElementsLoading } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/LayoutElements/useLayoutElementsLoading";
import {
  LAYOUT_ELEMENTS_TIMING_FETCH_END_KEY,
  LAYOUT_ELEMENTS_TIMING_FETCH_START_KEY,
  PageLoadTimingSectionName,
} from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/consts";
import { useCurrentNavigationPath } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useCurrentNavigationPath";

export const useCategoryLayoutElementsLoadTiming = (
  placement: ILayoutElement.Placement
) => {
  const isLayoutElementsLoading = useLayoutElementsLoading();
  const filtersTimingRef = useRef<ITraceEventMarker>();
  const navigationPath = useCurrentNavigationPath();
  const layoutElementFilterKeys = useLayoutElementFilterKeys();

  const previousFiltersRef = useRef<string[] | undefined>(undefined);

  const filterKeysChanged = useMemo(() => {
    if (!isEqual(previousFiltersRef.current, layoutElementFilterKeys)) {
      previousFiltersRef.current = layoutElementFilterKeys;
      return true;
    }

    return false;
  }, [layoutElementFilterKeys]);

  const trackMounted = useEffectEvent(() => {
    // data for all layout element placements is fetched together, so for ease of
    // tracking this PETE event we consider just the top-most placement BELOW_BREADCRUMB
    if (placement !== ILayoutElement.Placement.BELOW_BREADCRUMB) {
      return;
    }
    if (!filterKeysChanged) {
      return;
    }
    filtersTimingRef.current = trackCategoriesLoadPageSectionTiming(
      navigationPath,
      PageLoadTimingSectionName.LAYOUT_ELEMENTS
    );
  });

  useLayoutEffect(() => {
    trackMounted();
  }, [trackMounted, layoutElementFilterKeys, filterKeysChanged]);

  useLayoutEffect(() => {
    if (!filtersTimingRef.current) {
      return;
    }

    if (!isLayoutElementsLoading) {
      const startFetchTime =
        performance
          .getEntriesByName?.(LAYOUT_ELEMENTS_TIMING_FETCH_START_KEY)
          .slice(-1)?.[0]?.startTime ?? 0;
      const endFetchTime =
        performance
          .getEntriesByName?.(LAYOUT_ELEMENTS_TIMING_FETCH_END_KEY)
          .slice(-1)?.[0]?.startTime ?? 0;

      // don't emit API request start/end traces if the fetch started or
      // ended before mounting the component
      if (startFetchTime > filtersTimingRef.current.sourceEventStart) {
        trackCategoriesLoadPageSectionTimingTraceApiRequestStart(
          filtersTimingRef.current,
          { timestampOverride: startFetchTime }
        );
      }
      if (endFetchTime > filtersTimingRef.current.sourceEventStart) {
        trackCategoriesLoadPageSectionTimingTraceApiRequestEnd(
          filtersTimingRef.current,
          { timestampOverride: endFetchTime }
        );
      }

      trackCategoriesLoadPageSectionTimingTraceRendered(
        filtersTimingRef.current
      );
    }
  }, [isLayoutElementsLoading]);
};
